// Reviews.js - reviews written by customers after their order was delivered (real data)
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import './Reviews.css';
import { MdCalendarMonth, MdStar } from 'react-icons/md';
import { RiDeleteBin5Fill, RiReplyAllFill } from 'react-icons/ri';
import axiosInstance from '../../utils/axiosInstance';

const REFRESH_MS = 20000;

// Last loaded reviews of each shop are kept, so the list shows instantly
// on the next visit while fresh data loads in the background.
const REVIEWS_CACHE_PREFIX = 'reviews_cache_';

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {}
};

const toast = (icon, title) =>
  Swal.fire({ icon, title, toast: true, position: 'top-end', timer: 2200, showConfirmButton: false });

const errorText = (err, fallback) =>
  err?.response?.data?.error || err?.response?.data?.detail || fallback;

const formatDate = (iso, fallbackDate) => {
  const d = new Date(iso || fallbackDate || '');
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const Stars = ({ rating }) => (
  <div className="comm-star-row" aria-label={`${rating} out of 5`}>
    {[1, 2, 3, 4, 5].map((s) => (
      <span key={s} className={`comm-star ${s <= rating ? 'filled' : ''}`}>★</span>
    ))}
  </div>
);

const Reviews = () => {
  const { shopId } = useParams();
  const cacheKey = `${REVIEWS_CACHE_PREFIX}${shopId || 'all'}`;

  // The shop is known from the very first render, and the saved list of that shop
  // is shown instantly while fresh data loads in the background.
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    const cached = readJson(cacheKey);
    initialRef.current = Array.isArray(cached) ? cached : null;
  }

  const [reviews, setReviews] = useState(initialRef.current || []);
  // True only while there is nothing to show yet (so "No reviews yet" never flashes)
  const [isLoading, setIsLoading] = useState(!initialRef.current);
  const [error, setError] = useState('');
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const requestRef = useRef(0);
  const prevKeyRef = useRef(shopId || 'all');

  // local changes (delete / reply) are saved too, so the saved copy is always correct
  const updateReviews = (updater) => {
    setReviews((prev) => {
      const next = updater(prev);
      writeJson(cacheKey, next);
      return next;
    });
  };

  const load = useCallback(
    async (silent = false) => {
      // Only the latest request is allowed to update the screen
      const requestId = ++requestRef.current;
      try {
        const res = await axiosInstance.get('/feedback/reviews/', {
          params: shopId ? { shop_id: shopId } : {},
        });
        if (requestId !== requestRef.current) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setReviews(list);
        writeJson(cacheKey, list);
        setError('');
      } catch (err) {
        if (requestId !== requestRef.current) return;
        if (!silent) setError(errorText(err, 'Could not load reviews.'));
      } finally {
        if (requestId === requestRef.current) setIsLoading(false);
      }
    },
    [shopId, cacheKey]
  );

  useEffect(() => {
    const key = shopId || 'all';
    // Shop changed: show the saved list of this shop immediately (if there is one)
    if (prevKeyRef.current !== key) {
      prevKeyRef.current = key;
      const cached = readJson(cacheKey);
      if (Array.isArray(cached)) {
        setReviews(cached);
        setIsLoading(false);
      } else {
        setReviews([]);
        setIsLoading(true);
      }
    }

    load();
    const timer = setInterval(() => load(true), REFRESH_MS);
    const onFocus = () => load(true);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const average = useMemo(() => {
    if (!reviews.length) return 0;
    return reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length;
  }, [reviews]);

  const handleDelete = async (review) => {
    const result = await Swal.fire({
      title: 'Delete this review?',
      text: `${review.customer_name} - ${review.product_name}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete',
    });
    if (!result.isConfirmed) return;

    try {
      await axiosInstance.delete(`/feedback/reviews/${review.id}/`);
      updateReviews((prev) => prev.filter((r) => r.id !== review.id));
      toast('success', 'Review deleted');
    } catch (err) {
      toast('error', errorText(err, 'Could not delete the review.'));
    }
  };

  const handleReply = (review) => {
    setSelectedReview(review);
    setReplyText('');
    setShowReplyModal(true);
  };

  const closeModal = () => {
    if (sending) return;
    setShowReplyModal(false);
    setSelectedReview(null);
    setReplyText('');
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) {
      toast('warning', 'Please enter a reply message');
      return;
    }
    setSending(true);
    try {
      const res = await axiosInstance.post(`/feedback/reviews/${selectedReview.id}/reply/`, {
        reply: replyText.trim(),
      });
      updateReviews((prev) => prev.map((r) => (r.id === res.data.id ? res.data : r)));
      toast('success', `Reply sent to ${selectedReview.customer_email}`);
      setShowReplyModal(false);
      setSelectedReview(null);
      setReplyText('');
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Reply not sent', text: errorText(err, 'Something went wrong.'), confirmButtonColor: '#004aad' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="reviews-uk-container">
      <div className="reviews-header">
        <MdStar className="reviews-header-icon" />
        <h2>
          Reviews
          {reviews.length > 0 && (
            <span className="comm-count-badge" style={{ background: '#004aad' }}>
              {average.toFixed(1)} ★ ({reviews.length})
            </span>
          )}
        </h2>
      </div>

      <div className="comm-toolbar">
        <span className="comm-live"><span className="comm-live-dot" /> Live - updates every 20 s</span>
      </div>

      {error && <div className="comm-error">{error}</div>}

      <div className="reviews-grid">
        {/* Nothing is shown until the first load is done, so "No reviews yet" never flashes */}
        {isLoading ? (
          <div style={{ minHeight: '200px', gridColumn: '1 / -1' }} />
        ) : reviews.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📭</div>
            <p className="empty-state-text">No reviews yet</p>
          </div>
        ) : (
          reviews.map((review) => (
            <div key={review.id} className="review-card">
              <div className="review-card-header">
                <div className="review-avatar">{(review.customer_name || '?').charAt(0).toUpperCase()}</div>
                <div className="review-user-info">
                  <h3 className="review-user-name">{review.customer_name}</h3>
                  <p className="review-user-email">{review.customer_email}</p>
                  <p className="review-date">
                    <span><MdCalendarMonth /></span> {formatDate(review.created_at, review.reviewDate)}
                  </p>
                  <Stars rating={review.rating} />
                </div>
              </div>

              <div className="review-content">
                <div className="comm-tags">
                  <span className="comm-tag">{review.product_name}</span>
                  {review.order_id && <span className="comm-tag">Order #{review.order_id}</span>}
                  {review.shop_name && <span className="comm-tag shop">{review.shop_name}</span>}
                </div>

                <div className="review-text">{review.content || <em>No comment, rating only.</em>}</div>

                {review.reply && (
                  <div className="comm-replied">
                    <small>Replied {formatDate(review.replied_at)}</small>
                    {review.reply}
                  </div>
                )}

                <div className="review-actions">
                  <button className="review-reply-btn" onClick={() => handleReply(review)}>
                    <RiReplyAllFill /> {review.reply ? 'Reply again' : 'Reply'}
                  </button>
                  <button className="review-delete-btn" onClick={() => handleDelete(review)}>
                    <RiDeleteBin5Fill /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showReplyModal && selectedReview && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Reply to {selectedReview.customer_name}</h3>
            <p className="modal-subject">To: {selectedReview.customer_email} - {selectedReview.product_name}</p>
            <div className="comm-original">{selectedReview.content || `${selectedReview.rating} / 5`}</div>
            <textarea
              className="modal-textarea"
              rows="5"
              placeholder="Type your reply here... (sent to the customer by e-mail)"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
            />
            <div className="modal-buttons">
              <button className="modal-cancel-btn" onClick={closeModal} disabled={sending}>Cancel</button>
              <button className="modal-send-btn" onClick={handleSendReply} disabled={sending}>
                {sending ? 'Sending...' : 'Send Reply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reviews;